/* eslint-disable prettier/prettier */
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import InvestorService from "services/invoice-service";
import { handlingUrlParams } from "Utils/AppUtils";
import { Tag } from "primereact/tag";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";

import ViewPackages from "./ViewPackages";
import * as XLSX from "xlsx";
import { setToaster } from "common/toastStore";
import InvestorFilters from "./InvestorFilters";
import AddPackage from "components/investorAdmin/Addpackage";

import AdminServices from "services/admin-services";
import { useNavigate } from "react-router-dom";

const INVESTOR_FILTERS_STORAGE_KEY = "investor.list.filters";

const DEFAULT_FILTERS = {
  name: "",
  documentReference: "",
  externalInvestorName: "",
  isBondInvestor: null,
  pullCriteria: null,
  createdFrom: null,
  createdTo: null,
  includeInactive: true,
};

const parseStoredDate = (value: any) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getStoredInvestorFilters = () => {
  try {
    const raw = localStorage.getItem(INVESTOR_FILTERS_STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;

    const parsed = JSON.parse(raw);

    return {
      ...DEFAULT_FILTERS,
      ...parsed,
      createdFrom: parseStoredDate(parsed?.createdFrom),
      createdTo: parseStoredDate(parsed?.createdTo),
    };
  } catch {
    return DEFAULT_FILTERS;
  }
};

const Investor = () => {
  const navigate = useNavigate();
  const op = useRef<OverlayPanel>(null);
  const pullCriteriaOp = useRef<OverlayPanel>(null);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [investorData, setInvestorData] = useState([] as any);
  const [showPackageConfig, setShowPackageConfig] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState<any>(null);
  const [selectedPullCriteria, setSelectedPullCriteria] = useState<any>(null);
  const [packageData, setPackageData] = useState<any>(null);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<any>(
    getStoredInvestorFilters,
  );
  const [addPackageVisible, setAddPackageVisible] = useState(false);
  const [packageList, setPackageList] = useState([]);
  // const [criteriaList, setCriteriaList] = useState([]);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
  });

  const onPage = (event: any) => {
    setLazyParams({
      ...lazyParams,
      first: event.first, // index of first record in current page
      rows: event.rows, // rows per page
      page: event.page + 1, // 1-based page for backend
    });
  };

  const onSort = (event: any) => {
    setLazyParams(event);
  };

  const loadLazyData = () => {
    setLoading(true);

    const params: Record<string, any> = {
      PageNumber: lazyParams.page,
      PageSize: lazyParams.rows,
      IncludeDocumentType: true,
      IncludeInactive: appliedFilters.includeInactive ?? true,
    };

    if (appliedFilters.name?.trim()) {
      params.SearchTerm = appliedFilters.name.trim();
    }

    if (appliedFilters.documentReference?.trim()) {
      params.DocumentType = appliedFilters.documentReference.trim();
    }

    if (appliedFilters.externalInvestorName?.trim()) {
      params.ExternalInvestorName = appliedFilters.externalInvestorName.trim();
    }

    if (appliedFilters.isBondInvestor !== null) {
      params.IsBondInvestor = appliedFilters.isBondInvestor;
    }

    if (appliedFilters.pullCriteria) {
      params.PullCriteria = appliedFilters.pullCriteria;
    }

    if (appliedFilters.createdFrom) {
      params.StartDate = appliedFilters.createdFrom.toISOString();
    }

    if (appliedFilters.createdTo) {
      params.EndDate = appliedFilters.createdTo.toISOString();
    }

    InvestorService.getInvestorsList(handlingUrlParams(params))
      .then(async (response: any) => {
        const getHeaders = response.headers;
        const parsedHeaders = JSON.parse(getHeaders["x-pagination"]);
        setTotalRecords(parsedHeaders.TotalCount);
        setInvestorData(response.data);
        setLoading(false);
        await getPackagesByID(response.data[0]?.customerId);
        // await getPullCriteria(response.data[0]?.customerId);
      })
      .catch((e: Error) => {
        setLoading(false);
        console.log(e);
      });
  };

  const getPackages = (investorId: string) => {
    op.current?.hide();
    setLoading(true);
    InvestorService.getInvestorsPackage(investorId)
      .then((response: any) => {
        setPackageData(response?.data);
        if (response?.data && response?.data.length > 0) {
          setShowPackageConfig(true);
          setLoading(false);
        } else {
          setLoading(false);
          setAddPackageVisible(true);
          setToaster({
            severity: "info",
            summary: "No Packages",
            detail:
              "No packages were found for this investor. Please add a package.",
          });
        }
      })
      .catch((e: Error) => {
        setLoading(false);
        console.log(e);
      });
  };

  useEffect(() => {
    loadLazyData();
  }, [lazyParams, appliedFilters]);

  useEffect(() => {
    localStorage.setItem(
      INVESTOR_FILTERS_STORAGE_KEY,
      JSON.stringify(appliedFilters),
    );
  }, [appliedFilters]);

  const pullCriteria = (rowData: any) => {
    const pull = rowData?.pullCriteria;
    const raw = pull?.searchCriteria;

    if (!pull) return <>N/A</>;

    let parsedData: any;

    try {
      if (raw) {
        try {
          parsedData = JSON.parse(raw);
        } catch {
          const safeJson = raw.replace(
            /@@investorlist@@/gi,
            '"@@InvestorList@@"',
          );
          parsedData = JSON.parse(safeJson);
        }
      }
    } catch (e) {
      console.error("Invalid searchCriteria JSON:", raw);
      return <>Invalid criteria</>;
    }

    const fields: string[] = parsedData?.fields || [];
    const filters: any[] = parsedData?.filter?.terms || [];

    return (
      <div
        className="text-sm leading-relaxed max-w-md"
        style={{ minHeight: 120 }}
      >
        <div className="mb-2">
          <div className="font-semibold">{pull.name}</div>
          {pull.description && (
            <div className="text-gray-600">{pull.description}</div>
          )}
        </div>

        <>
          {fields.length > 0 && (
            <div className="mb-2">
              <strong>Fields:</strong>
              <ul className="list-disc list-inside">
                {fields.map((field, idx) => (
                  <li key={idx}>{field}</li>
                ))}
              </ul>
            </div>
          )}

          {filters.length > 0 && (
            <div>
              <strong>Filters:</strong>
              <ul className="list-disc list-inside">
                {filters.map((term, idx) => {
                  let valueLabel = "—";

                  if (term.matchType === "IsEmpty") valueLabel = "is empty";
                  else if (term.matchType === "MultiValue")
                    valueLabel = "@@InvestorList@@";
                  else if (term.value)
                    valueLabel = Array.isArray(term.value)
                      ? term.value.join(", ")
                      : term.value;

                  return (
                    <li key={idx}>
                      <span className="font-medium">{term.canonicalName}</span>{" "}
                      <span className="text-gray-500">({term.matchType})</span>:{" "}
                      {valueLabel}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      </div>
    );
  };

  const pullCriteriaTemplate = (rowData: any) => {
    if (!rowData?.pullCriteria) return <>N/A</>;

    return (
      <div className="flex flex-row items-center">
        <span>{rowData?.pullCriteria?.name} </span>
        <i
          className="pi pi-info-circle mx-2 text-blue-500 cursor-pointer"
          onClick={(e) => {
            setSelectedPullCriteria(rowData);
            pullCriteriaOp.current?.toggle(e);
          }}
        />
      </div>
    );
  };

  // const showOverpanel = (e: any, data: any) => {
  //   console.log("Row Data for OverlayPanel:", data);
  //   op.current?.toggle(e);
  //   setSelectedInvestor(data);
  // };

  const renderActions = (rowData: any) => {
    return (
      <>
        <Button
          type="button"
          className="p-button-sm p-button-outlined p-button-rounded p-button-primary"
          icon="pi pi-eye"
          onClick={() => {
            navigate(`/investor/${rowData?.id}`);
          }}
        />
      </>
    );
  };

  const bondInvestorTemplate = (rowData: any) => {
    const isBond = rowData?.isBondInvestor === true;

    return (
      <Tag
        value={isBond ? "Yes" : "No"}
        severity={isBond ? "success" : "info"}
      />
    );
  };

  const ClassificationTemplate = (rowData: any) => {
    const isBond = rowData?.enableClassification === true;

    return (
      <Tag
        value={isBond ? "Yes" : "No"}
        severity={isBond ? "success" : "info"}
      />
    );
  };

  const activeTemplate = (rowData: any) => {
    const isActive = rowData?.isActive === true;

    return (
      <Tag
        value={isActive ? "Yes" : "No"}
        severity={isActive ? "success" : "danger"}
      />
    );
  };

  const hidePackageModal = () => {
    setShowPackageConfig(false);
    setSelectedInvestor(null);
    loadLazyData();
  };

  const handleApplyFilters = (filters: any) => {
    setLazyParams((prev) => ({ ...prev, page: 1, first: 0 }));
    setAppliedFilters(filters);
    setFiltersVisible(false);
  };

  const getPackagesByID = (customerId: number) => {
    AdminServices.getInvestorPackagesNew(
      handlingUrlParams({
        customerId,
      }),
    )
      .then((response: any) => {
        const packages = (response?.data || []).map((pkg: any) => ({
          label: pkg?.package?.name,
          value: Number(pkg?.package?.id ?? pkg?.packageId ?? pkg?.id ?? 0),
          investorPackageId: pkg.id,
          packageData: pkg,
        }));

        setPackageList(packages);
      })
      .catch((e: Error) => {
        console.log(e);
      });
  };

  const buildParams = (overrides: Record<string, any> = {}) => {
    const params: Record<string, any> = {
      PageNumber: lazyParams.page,
      PageSize: lazyParams.rows,
      IncludeDocumentType: true,
      IncludeInactive: appliedFilters.includeInactive ?? true,
      ...overrides,
    };

    if (appliedFilters.name?.trim())
      params.SearchTerm = appliedFilters.name.trim();
    if (appliedFilters.documentReference?.trim())
      params.DocumentType = appliedFilters.documentReference.trim();
    if (appliedFilters.externalInvestorName?.trim())
      params.ExternalInvestorName = appliedFilters.externalInvestorName.trim();
    if (appliedFilters.isBondInvestor !== null)
      params.IsBondInvestor = appliedFilters.isBondInvestor;
    if (appliedFilters.pullCriteria)
      params.PullCriteria = appliedFilters.pullCriteria;
    if (appliedFilters.createdFrom)
      params.StartDate = appliedFilters.createdFrom.toISOString();
    if (appliedFilters.createdTo)
      params.EndDate = appliedFilters.createdTo.toISOString();

    return params;
  };

  const exportData = async () => {
    setLoading(true);
    try {
      const params = buildParams({ PageNumber: 1, PageSize: 500 });
      const response: any = await InvestorService.exportInvestorsList(
        handlingUrlParams(params),
      );
      const data = response?.data || [];
      console.log("Exported data:", data);
      if (!data || data.length === 0) {
        setToaster({
          severity: "info",
          summary: "No data",
          detail: "No records to export.",
        });
        setLoading(false);
        return;
      }

      const exportRows = data.map((item: any) => ({
        Id: item?.id,
        "Customer Name": item?.customerName,
        Name: item?.name,
        Description: item?.description,
        "Pull Criteria Name": item?.pullCriteria?.name,
        "Enable Classification": item?.enableClassification,
        "Bond Investor": item?.isBondInvestor,
        "External Investor Name": item?.externalInvestorName,
        Active: item?.isActive,
      }));

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Investors");

      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `investors_export_${new Date().toISOString()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error(e);
      setToaster({
        severity: "error",
        summary: "Export failed",
        detail: e?.message || "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DataTable
        className="p-datatable-sm"
        scrollable
        value={investorData}
        loading={loading}
        stripedRows
        lazy
        paginator
        filterDisplay="menu"
        responsiveLayout="scroll"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        dataKey="id"
        rowsPerPageOptions={[5, 10, 20, 50]}
        emptyMessage="No Jobs found."
        totalRecords={totalRecords}
        rows={lazyParams.rows}
        first={lazyParams.first}
        onPage={onPage}
        onSort={onSort}
        sortOrder={lazyParams.sortOrder}
        header={
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">List of Investors</h2>
            <div className="gap-4 flex flex-row">
              <Button
                onClick={() => {
                  navigate(`/addInvestor`);
                }}
                icon="pi pi-plus"
                label="Add Investor"
                className="p-button-sm p-button-outlined p-button-success"
              />
              <Button
                icon="pi pi-filter"
                label="Filters"
                onClick={() => setFiltersVisible(true)}
                className="p-button-sm p-button-outlined p-button-warning"
              />
              <Button
                icon="pi pi-file-export"
                label="Export"
                className="p-button-sm p-button-outlined p-button-primary"
                onClick={() => exportData()}
              />
            </div>
          </div>
        }
      >
        <Column field="name" header="Name" />
        <Column field="description" header="Description" />
        <Column header="Bond Investor" body={bondInvestorTemplate} />
        <Column body={pullCriteriaTemplate} header="Pull Criteria"></Column>
        <Column header="Enable Classification" body={ClassificationTemplate} />
        <Column field="externalInvestorName" header="External Investor Name" />
        <Column header="Active" body={activeTemplate} />
        <Column body={renderActions} header="Options"></Column>
      </DataTable>

      <OverlayPanel ref={pullCriteriaOp} style={{ width: 400 }}>
        {selectedPullCriteria?.pullCriteria ? (
          pullCriteria(selectedPullCriteria)
        ) : (
          <p className="text-gray-500">No criteria available</p>
        )}
      </OverlayPanel>

      <Sidebar
        visible={showPackageConfig}
        position="right"
        style={{ width: "80vw" }}
        onHide={() => setShowPackageConfig(false)}
      >
        <ViewPackages
          data={packageData}
          hidePackageModal={hidePackageModal}
          packageList={packageList}
          selectedInvestor={selectedInvestor}
          refreshPackages={() => getPackages(selectedInvestor.id)}
        />
      </Sidebar>

      <Sidebar
        visible={filtersVisible}
        position="right"
        onHide={() => setFiltersVisible(false)}
        style={{ width: 520 }}
      >
        <InvestorFilters
          onHideFilters={() => setFiltersVisible(false)}
          onApplyFilters={handleApplyFilters}
          customerId={investorData?.[0]?.customerId}
          initialValues={appliedFilters}
        />
      </Sidebar>

      <Sidebar
        visible={addPackageVisible}
        position="right"
        onHide={() => setAddPackageVisible(false)}
        style={{ width: 520 }}
      >
        <AddPackage
          onClose={() => setAddPackageVisible(false)}
          customerId={investorData?.[0]?.customerId}
          onSubmitSuccess={loadLazyData}
        />
      </Sidebar>
    </>
  );
};

export default Investor;
